import React, { Component } from "react"
import ComputerTestSAT from "./ComputerTestSAT"
import { SAT_BREAK_TIME_IN_SECONDS, SAT_SECTION_MODULE_KEYS, saveTestStartTime, TestTimeIndicator } from "../../../../services/utils/practice-test-util"
import WelcomePage from "./pages/WelcomePage"
import TimeSelectorPage from "./pages/TimeSelectorPage"
import BreakInfoPage from "./pages/BreakInfoPage"
import ModuleOverPage from "./pages/ModuleOverPage"
import TestOverPage from "./pages/TestOverPage"
import { SimpleTimer } from "../../../../services/utils/simple-timer"
import { Context, Resource, sendUserActivity, UserActivityEx } from "../../../../services/liveview/liveview-service"

/**
 * SAT Workflow:
 *  + Welcome
 *  + Start
 *  + Time select: Regular, 1.5x, 2x
 *  + R&W1
 *  + R&W2
 *  + Break
 *      . 10 min countdown
 *      . Can click resume to continue
 * + Math1
 * + Math2
 * + Ending Page
 *      . Message
 *      . Take student to the SAT Dashboard
 * 
 */

interface Props {
    userInfo: any
    testDetails: any
    onTestExit: (testName: string) => void
}

interface State {
    currentStep: number
    steps: any[]
    testTimeIndicator: TestTimeIndicator
}

export default class WorkflowSAT extends Component<Props, State> {
    simpleTimer = new SimpleTimer()

    constructor(props) {
        super(props)
        this.state = {
            currentStep: 0,
            steps: [],
            testTimeIndicator: "regular",
        }
    }

    componentDidMount(): void {
        this.initializeSteps()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.testDetails !== this.props.testDetails) {
            this.initializeSteps()
        }
    }

    componentWillUnmount() {

    }

    /**
     * In the Computer-Based Virtaul Practice Tests a user can exit and resume the test many times
     * so we need to allow multiple entries for this resource, hence the sendUseractivity has 
     * a boolean flag set to true.
     */
    sendStats(args?: {start?: boolean, end?: boolean, moduleCategory?: string}) {
        let {testDetails} = this.props
        let {testName} = testDetails
        let seconds = this.simpleTimer.diffInSeconds()
        let ended = seconds
        let category = "PracticeTestSAT"
        let context = Context.COMPUTER_TEST
        let contextId = `${testName}`
        let resource = Resource.TEST_SESSION
        let resourceId = `${testName}`
        let userActivityEx: UserActivityEx = {
            context, contextId, 
            resource, resourceId, 
            category,
            ended
        }

        if (args) {
            let {start=false, end=false, moduleCategory} = args
            if (start) {
                userActivityEx = {...userActivityEx, resource: Resource.START}
            }
            else if (end) {
                userActivityEx = {...userActivityEx, resource: Resource.END}
            }
            if (moduleCategory) {
                userActivityEx = {...userActivityEx, category: moduleCategory}
            }
        }

        sendUserActivity(userActivityEx, true)
        this.simpleTimer.reset()
    }

    handleNext = () => {
        let {currentStep} = this.state
        ++currentStep
        this.setState({currentStep})
    }

    handleTestTimeSelection = (testTimeIndicator: TestTimeIndicator) => {
        let {currentStep} = this.state
        let {testDetails} = this.props

        testDetails.testTimeIndicator = testTimeIndicator

        ++currentStep
        this.setState({currentStep, testTimeIndicator})
    }

    handleTestExit = () => {
        let {onTestExit} = this.props
        let {testDetails} = this.props
        let {testName} = testDetails
        onTestExit(testName)
    }

    /**
     * Will find the correct starting steps based on the current module
     * @param currentModuleNameKey 
     * @returns 
     */
    findStartingStepBasedOnModuleName = (currentModuleNameKey) => {
        let startingStep = 0
        switch (currentModuleNameKey) {
            case SAT_SECTION_MODULE_KEYS.RW_MODULE1:
                startingStep = 1
                break

            case SAT_SECTION_MODULE_KEYS.RW_MODULE2:
                startingStep = 3
                break

            case SAT_SECTION_MODULE_KEYS.MATH_MODULE1:
                startingStep = 6
                break

            case SAT_SECTION_MODULE_KEYS.MATH_MODULE2:
                startingStep = 8
                break
        }
        return startingStep
    }

    initializeSteps = () => {
        let {userInfo, testDetails } = this.props

        if (!testDetails) return

        let {displayName, currentModuleNameKey} = testDetails
        let currentStep = 0
        let breakDurationInSeconds = SAT_BREAK_TIME_IN_SECONDS

        // If the test has already started then find the correct step to continue!
        if (currentModuleNameKey) {
            currentStep = this.findStartingStepBasedOnModuleName(currentModuleNameKey)
        }
        else {
            // Test starting...
            this.submitTestStart(testDetails)
        }

        let steps = [
            // Step 0
            <WelcomePage title={displayName} onTimeSelected={this.handleTestTimeSelection} />,

            // R&W Test
            // Step 1
            <ComputerTestSAT
                userInfo={userInfo}
                testDetails={testDetails}
                onNext={this.handleNext}
                onTestExit={this.handleTestExit}
                currentModuleNameKey={SAT_SECTION_MODULE_KEYS.RW_MODULE1}
                onPageUnmount={() => this.sendStats({moduleCategory: SAT_SECTION_MODULE_KEYS.RW_MODULE1})}
            />,

            // Step 2
            <ModuleOverPage title={"Reading & Writing Module 1"} onNext={this.handleNext} />,

            // Step 3
            <ComputerTestSAT 
                userInfo={userInfo}
                testDetails={testDetails}
                onNext={this.handleNext}
                onTestExit={this.handleTestExit}
                currentModuleNameKey={SAT_SECTION_MODULE_KEYS.RW_MODULE2}
                onPageUnmount={() => this.sendStats({moduleCategory: SAT_SECTION_MODULE_KEYS.RW_MODULE2})}
            />,

            // Step 4
            <ModuleOverPage title={"Reading & Writing Module 2"} onNext={this.handleNext} />,

            // Step 5
            <BreakInfoPage userInfo={userInfo} durationInSeconds={breakDurationInSeconds} onNext={this.handleNext} />,

            // Math Test
            // Step 6
            <ComputerTestSAT 
                userInfo={userInfo}
                testDetails={testDetails}
                onNext={this.handleNext}
                onTestExit={this.handleTestExit}
                currentModuleNameKey={SAT_SECTION_MODULE_KEYS.MATH_MODULE1}
                onPageUnmount={() => this.sendStats({moduleCategory: SAT_SECTION_MODULE_KEYS.MATH_MODULE1})}
            />,

            // Step 7
            <ModuleOverPage title={"Math Module 1"} onNext={this.handleNext} />,

            // Step 8
            <ComputerTestSAT 
                userInfo={userInfo}
                testDetails={testDetails}
                onNext={this.handleNext}
                onTestExit={this.handleTestExit}
                currentModuleNameKey={SAT_SECTION_MODULE_KEYS.MATH_MODULE2}
                onPageUnmount={() => this.sendStats({moduleCategory: SAT_SECTION_MODULE_KEYS.MATH_MODULE2})}
            />,

            // Step 9
            <TestOverPage title={displayName} onPageShown={this.onTestOverPageShown} onNext={this.handleTestExit} />,
        ]

        this.setState({currentStep, steps})
    }

    submitTestStart = (testDetails) => {
        testDetails.startedTime = new Date().toISOString()
        saveTestStartTime(testDetails)
        this.sendStats({start: true})
    }

    onTestOverPageShown = () => {
        // Computer-based Practice Test is finished!
        setTimeout(() => {
            this.sendStats({end: true})
        }, 300)
    }

    render() {
        let {steps, currentStep} = this.state

        return (
            <>
                {steps[currentStep]}
            </>
        )
    }
}
